<script>
import {
  required,
  integer,
  minValue,
  maxValue,
  requiredIf
} from 'vuelidate/lib/validators'

export default {
  name: 'pkf-form',
  props: {
    value: { type: Object, default: () => {} }
  },

  data: vm => ({
    form: {
      specs: {
        foam_type: null,
        seat_depth: 30,
        seat_width: 30,
        model_type: null
      },
      ...vm.value
    }
  }),
  computed: {
    FOAM_TYPES () {
      return [
        { value: null, text: ' - Select a Foam Quality -' },
        { value: 'framefoam', text: this.$t('attribute.foam_type.framefoam') },
        { value: 'f32', text: this.$t('attribute.foam_type.f32') },
        { value: 'rg50', text: this.$t('attribute.foam_type.rg50') },
        { value: 'rg70', text: this.$t('attribute.foam_type.rg70') },
        { value: 's4044', text: this.$t('attribute.foam_type.s4044') },
        { value: 'ld24', text: this.$t('attribute.foam_type.ld24') }
      ]
    },
    MODEL_TYPES () {
      return [
        { value: null, text: ' - Select Model Type -' },
        {
          value: 'low_abduction',
          text: this.$t('attribute.std_seat_type.low_abduction')
        },
        { value: 'acto', text: this.$t('attribute.std_seat_type.acto') },
        { value: 'assmann', text: this.$t('attribute.std_seat_type.assmann') },
        {
          value: 'assmann_high',
          text: this.$t('attribute.std_seat_type.assmann_high')
        },
        {
          value: 'hq_matrix',
          text: this.$t('attribute.std_seat_type.hq_matrix')
        },
        {
          value: 'hq_klein',
          text: this.$t('attribute.std_seat_type.hq_klein')
        },
        {
          value: 'hodey',
          text: this.$t('attribute.std_seat_type.hodey')
        }
      ]
    },
    HODEY_SIZES () {
      return [
        { value: null, text: ' - Select Seat Width -' },
        { value: 40, text: '40cm' },
        { value: 43, text: '43cm' },
        { value: 48, text: '48cm' },
        { value: 50, text: '50cm' }
      ]
    },
    hodeyEnabled () {
      return this.form.specs.model_type === 'hodey'
    }
  },
  watch: {
    'form.specs.model_type': {
      handler (val) {
        if (val === 'hodey') {
          this.form.specs.seat_width = null
          this.form.specs.seat_depth = 45
        } else {
          this.form.specs.seat_width = 30
          this.form.specs.seat_depth = 30
        }
      }
    }
  },
  validations: {
    form: {
      specs: {
        foam_type: {
          required
        },
        model_type: {
          required
        },
        seat_depth: {
          required: requiredIf(model => !model.hodeyEnabled),
          integer,
          minValue: minValue(20),
          maxValue: maxValue(70)
        },
        seat_width: {
          required,
          integer,
          minValue: minValue(20),
          maxValue: maxValue(70)
        }
      }
    }
  },
  methods: {
    validate () {
      if (!this.$v && !this.$v.form) return true
      this.$v.form.$touch()
      if (!this.$v.form.$invalid) {
        const payload = {
          specs: { ...this.form.specs },
          comments: this.form.comments
        }
        this.$emit('validated', payload)
        this.$emit('input', payload)
      }

      return !this.$v.form.$invalid
    }
  }
}
</script>
<template>
  <b-row>
    <b-col cols="12">
      <g-form-error-wrapper :validations="$v.form.specs.model_type">
        <template v-slot="{ errors, invalid }">
          <g-select
            v-model.number="form.specs.model_type"
            :label="$t('attribute.std_seat_type.key')"
            :options="MODEL_TYPES"
            buttons
            button-variant="outline-primary"
            :error-messages="errors"
            :is-valid="!invalid"
            v-on:change="$v.form.specs.model_type.$touch()"
            label-cols-sm="6"
            label-cols-lg="4"
            max-width="280px"
          >
          </g-select>
        </template>
      </g-form-error-wrapper>
      <g-form-error-wrapper :validations="$v.form.specs.foam_type">
        <template v-slot="{ errors, invalid }">
          <g-select
            v-model.number="form.specs.foam_type"
            :label="$t('attribute.foam_type.key')"
            :options="FOAM_TYPES"
            buttons
            button-variant="outline-primary"
            :error-messages="errors"
            :is-valid="!invalid"
            v-on:change="$v.form.specs.foam_type.$touch()"
            label-cols-sm="6"
            label-cols-lg="4"
            max-width="280px"
          >
          </g-select>
        </template>
      </g-form-error-wrapper>
      <g-form-error-wrapper
        :validations="$v.form.specs.seat_width"
        v-if="!hodeyEnabled"
      >
        <template v-slot="{ errors, invalid }">
          <g-text-input
            v-model.number="form.specs.seat_width"
            :label="$t('Seat Width')"
            append="cm"
            maxWidth="100px"
            text-align="center"
            :error-messages="errors"
            :is-valid="!invalid"
            v-on:blur="$v.form.specs.seat_width.$touch()"
            label-cols-sm="6"
            label-cols-lg="4"
          >
          </g-text-input>
        </template>
      </g-form-error-wrapper>
      <g-form-error-wrapper
        :validations="$v.form.specs.seat_width"
        v-if="hodeyEnabled"
      >
        <template v-slot="{ errors, invalid }">
          <g-select
            v-model.number="form.specs.seat_width"
            :label="$t('Seat Width')"
            :options="HODEY_SIZES"
            buttons
            button-variant="outline-primary"
            :error-messages="errors"
            :is-valid="!invalid"
            v-on:change="$v.form.specs.seat_width.$touch()"
            label-cols-sm="6"
            label-cols-lg="4"
            max-width="280px"
          >
          </g-select>
        </template>
      </g-form-error-wrapper>
      <g-form-error-wrapper
        :validations="$v.form.specs.seat_depth"
        v-if="!hodeyEnabled"
      >
        <template v-slot="{ errors, invalid }">
          <g-text-input
            v-model.number="form.specs.seat_depth"
            :label="$t('Seat Depth')"
            append="cm"
            maxWidth="100px"
            text-align="center"
            :error-messages="errors"
            :is-valid="!invalid"
            v-on:blur="$v.form.specs.seat_depth.$touch()"
            label-cols-sm="6"
            label-cols-lg="4"
          >
          </g-text-input>
        </template>
      </g-form-error-wrapper>

      <label>Comments</label>
      <b-form-textarea
        id="comments"
        placeholder="Extra comments or requirements"
        v-model="form.comments"
        rows="4"
        max-rows="4"
      />
    </b-col>
  </b-row>
</template>
